export function privateMediaPath(value, origin) {
  try {
    const parsed = new URL(value, origin);
    return parsed.origin === origin && /^\/api\/v1\/accounts\/[1-9]\d*\/private_media\/[1-9]\d*$/.test(parsed.pathname);
  } catch { return false; }
}

export function isTransientMediaError(error) {
  const status = error?.response?.status;
  return [408, 429, 502, 503, 504].includes(status) || status >= 500 ||
    ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error?.code);
}

export async function fetchMediaWithRetry(fetchBlob, source, wait = ms => new Promise(resolve => setTimeout(resolve, ms))) {
  for (let attempt = 0; ; attempt++) {
    try { return await fetchBlob(source); }
    catch (error) {
      if (attempt >= 2 || !isTransientMediaError(error)) throw error;
      await wait(500 * 2 ** attempt);
    }
  }
}

// A rejected request must never poison subsequent acquisitions of this resource.
// Existing consumers retain their lease until unmount/retry, without retaining bytes.
export function createMediaPool({ fetchBlob, createUrl, revokeUrl }) {
  const entries = new Map();
  return {
    invalidate(key) { entries.delete(key); },
    acquire(key, source) {
      let entry = entries.get(key);
      if (!entry) {
        entry = { refs: 0, objectUrl: null };
        const owned = entry;
        entry.promise = Promise.resolve().then(() => fetchBlob(source)).then(blob => {
          owned.objectUrl = createUrl(blob);
          return owned.objectUrl;
        }).catch(error => {
          if (entries.get(key) === owned) entries.delete(key);
          throw error;
        });
        entries.set(key, entry);
      }
      entry.refs++;
      let released = false;
      return { promise: entry.promise, release() {
        if (released) return;
        released = true;
        entry.refs--;
        queueMicrotask(() => {
          if (entry.refs !== 0) return;
          if (entries.get(key) === entry) entries.delete(key);
          entry.promise.then(() => revokeUrl(entry.objectUrl), () => {});
        });
      } };
    },
  };
}
